import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { api } from '../../core/client';
import type {
  AlarmHistoryFilters,
  AlarmHistoryPage,
  AlarmRecord,
  FleetOverview,
  SocSummary,
} from './alerting.types';

const ALERTING = '/users/v1/alerting';
const KEY = ['alerting'] as const;

/** `enabled` gates the fetch for callers whose user may lack `maintenance:read` (R-085 overview). */
export function useAlarms(openOnly: boolean, enabled = true) {
  return useQuery({
    queryKey: [...KEY, 'alarms', openOnly],
    queryFn: () => api.get<AlarmRecord[]>(`${ALERTING}/alarms?open_only=${openOnly}`),
    refetchInterval: 30_000,
    enabled,
  });
}

/** R-173: ONE alarm by id — what a notification deep link resolves to. Addressable
 *  on its own so a link works for ANY alarm, not only one that happens to sit in a
 *  page the SOC already loaded (and regardless of the filters in effect there). */
export function useAlarm(id: string | null | undefined) {
  return useQuery({
    queryKey: [...KEY, 'alarm', id],
    queryFn: () => api.get<AlarmRecord>(`${ALERTING}/alarms/${id}`),
    enabled: !!id,
  });
}

/** R-112: paginated alarm HISTORY with status/severity filters (SOC history view). */
export function useAlarmsHistory(filters: AlarmHistoryFilters, enabled = true) {
  return useInfiniteQuery({
    queryKey: [...KEY, 'history', filters],
    queryFn: ({ pageParam }) => {
      const q = new URLSearchParams();
      if (filters.status) q.set('status', filters.status);
      if (filters.severity) q.set('severity', filters.severity);
      q.set('limit', '30');
      if (pageParam) q.set('cursor', pageParam as string);
      return api.get<AlarmHistoryPage>(`${ALERTING}/alarms/history?${q.toString()}`);
    },
    initialPageParam: '',
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    // A filter change keeps the current rows up (dimmed by the ticker) until the
    // new filter's first page lands.
    placeholderData: keepPreviousData,
    enabled,
  });
}

/** R-085: the cloud landing page's fleet rollup — plain-auth, every cloud user. */
export function useFleetOverview(enabled = true) {
  return useQuery({
    queryKey: [...KEY, 'fleet'],
    queryFn: () => api.get<FleetOverview>('/users/v1/overview'),
    refetchInterval: 30_000,
    enabled,
  });
}

export function useSocSummary() {
  return useQuery({
    queryKey: [...KEY, 'soc'],
    queryFn: () => api.get<SocSummary>(`${ALERTING}/soc`),
    refetchInterval: 30_000,
  });
}

export function useAckAlarm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<AlarmRecord>(`${ALERTING}/alarms/${id}/ack`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useResolveAlarm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) =>
      api.post<AlarmRecord>(`${ALERTING}/alarms/${id}/resolve`, { note }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
